import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { Theme } from '@mui/material/styles';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import DevMenuButton from 'components/DevMenuButton';
import Dump from 'components/Dump';

import BasicTab from '../tabs/basicTab';
import BillConnectTab from '../tabs/billConnectTab';
import FrequencyTab from '../tabs/frequencyTab';
import type { ScheduledTransactionEditViewModel } from './ScheduledTransactionEditViewModel';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';

const useStyles = makeStyles()((theme: Theme) => ({
  tabs: {
    display: 'flex',
    flexShrink: 0,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
  },
  tabPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.border.main}`,
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
      width: '100%',
      boxSizing: 'border-box',
    },
  },
  billConnect: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  hidden: {
    display: 'none',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    overflow: 'hidden',
    '#frm-root-basic': {
      padding: theme.spacing(3, 0, 4, 0),
      '> div > div': {
        padding: theme.spacing(0, 3),
      },
    },
  },
}));

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

function TabPanel({ children, index, value, ...other }: TabPanelProps): React.JSX.Element {
  const { classes } = useStyles();

  return (
    <div
      role='tabpanel'
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      className={value === index ? classes.tabPanel : classes.hidden}
    >
      {value === index && children}
    </div>
  );
}

type Props = {
  viewModel: ScheduledTransactionEditViewModel;
};

export default function ScheduledTransactionEditFormContent({ viewModel }: Props): React.JSX.Element {
  const { classes } = useStyles();
  const {
    actions: {
      confirmDelete,
      confirmEndSeries,
      forceSubmit,
      generateSeriesInstances,
      generateSeriesTransferInstances,
      handleSubmit,
      manualSeriesApiCall,
      openAssignSplits,
      setTabValue,
    },
    derived: {
      create,
      minimumDate,
      nestedRootModalDrawer,
      scheduledTransaction,
      showBillTab,
      subscriptionInitial,
      tabValue,
    },
    form,
  } = viewModel;

  const handleChange = (_event: unknown, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <Dump value={{ scheduledTransaction, form }} id={scheduledTransaction?.id} />

      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label='custom frequency tabs'
        className={classes.tabs}
        variant='fullWidth'
      >
        <Tab label='Basic Details' />
        <Tab label='Frequency & Occurrence' />
        {showBillTab && <Tab label='Bill Connect' />}
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <StdDialogContent id='frm-root-basic'>
          <BasicTab
            form={form}
            scheduledTransaction={scheduledTransaction}
            setTabValue={setTabValue}
            create={create}
            subscriptionInitial={subscriptionInitial}
            showBillConnect={showBillTab}
            onAssignSplits={openAssignSplits}
            onEndSeries={confirmEndSeries}
          />
        </StdDialogContent>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <StdDialogContent id='frm-root-frequency'>
          <FrequencyTab form={form} minimumDate={minimumDate} />
        </StdDialogContent>
      </TabPanel>

      {showBillTab && (
        <TabPanel value={tabValue} index={2}>
          <StdDialogContent className={classes.billConnect} id='frm-root-bill-connect'>
            <BillConnectTab form={form} rootModalDrawer={nestedRootModalDrawer} />
          </StdDialogContent>
        </TabPanel>
      )}

      <StdDialogActions
        connectionBanner='replace'
        primary={{
          id: 'create-update-recurrence',
          children: create ? 'Create' : 'Update',
        }}
        secondary={{
          id: 'edit-recurrence-cancel',
          children: 'Cancel',
          onClick: viewModel.actions.onClose,
        }}
        tertiaryAction={
          create
            ? undefined
            : {
                id: 'delete-recurrence',
                children: 'Delete',
                variant: 'outlined',
                color: 'error',
                onClick: confirmDelete,
              }
        }
        tertiaryNode={
          <DevMenuButton id='recurrence-dev-menu'>
            <MenuItem onClick={forceSubmit}>Force Series Submit</MenuItem>
            <MenuItem onClick={manualSeriesApiCall}>Manual Series API Call</MenuItem>
            <MenuItem onClick={generateSeriesInstances}>Generate Series Instances</MenuItem>
            <MenuItem onClick={generateSeriesTransferInstances}>Generate Series Transfer Instances</MenuItem>
          </DevMenuButton>
        }
      />
    </form>
  );
}
